import { useRef, useMemo, FC, ReactNode, PropsWithChildren } from 'react';

import ChartContext from 'src/components/LineChart/ChartContext';
import { LineChart, LineChartRawData } from 'src/components/LineChart/types';
import {
    getBorderAxesValues,
    getBorderAxesValuesForMultipleLines,
    getPreparedData,
    getXAxis,
    getYAxis,
    getLinePath,
    getAreaPath,
} from 'src/components/LineChart/utils';

import styles from 'src/components/LineChart/line-chart.less';

export interface ChartProps {
    width: number;
    height: number;
    data: LineChartRawData[];
    xAxisMin?: Date;
    xAxisMax?: Date;
    yAxisMin?: number;
    yAxisMax?: number;
    separatedYAxes?: boolean;
    renderAdditionalContentBefore?: (data: LineChart) => ReactNode;
    renderAdditionalContentAfter?: (data: LineChart) => ReactNode;
    /**
     * Позволяет использовать children, вне svg-контейнера
     */
    customChart?: boolean;
    /**
     * Позволят выводить точки графика кратные указанному числу
     */
    chartDimension?: number;
    /**
     * Добавляет плавной сглаженности графику
     */
    isCurveChart?: boolean;
}

const Chart: FC<ChartProps & PropsWithChildren> = ({
    width,
    height,
    data,
    xAxisMin,
    xAxisMax,
    yAxisMin,
    yAxisMax,
    separatedYAxes = false,
    renderAdditionalContentBefore,
    renderAdditionalContentAfter,
    customChart = false,
    chartDimension = 0,
    isCurveChart = false,
    children,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // подготавливаем данные для графиков и осей
    const chartData = useMemo(() => {
        const commonBorderAxesValues = getBorderAxesValuesForMultipleLines(data);
        return data.map((lineData) => {
            const preparedData = getPreparedData(lineData);
            const borderAxesValues = getBorderAxesValues(lineData);

            // Ось OX всегда общая у нескольких линий на одном графике
            const xAxis = getXAxis(
                xAxisMin !== undefined ? xAxisMin : commonBorderAxesValues.date.min,
                xAxisMax !== undefined ? xAxisMax : commonBorderAxesValues.date.max,
                width
            );
            // Ось OY может быть разной для двух линий на графике (слева одна шкала, справа другая)
            const borderYAxes = separatedYAxes ? borderAxesValues.value : commonBorderAxesValues.value;
            const yAxis = getYAxis(
                yAxisMin !== undefined ? yAxisMin : borderYAxes.min,
                yAxisMax !== undefined ? yAxisMax : borderYAxes.max,
                height
            );

            const linePath = getLinePath(preparedData, xAxis, yAxis, isCurveChart);
            const areaPath = getAreaPath(preparedData, xAxis, yAxis, borderAxesValues.value.min, isCurveChart);
            return { data: preparedData, xAxis, yAxis, linePath, areaPath };
        });
    }, [data, width, height, separatedYAxes, xAxisMin, xAxisMax, yAxisMin, yAxisMax, isCurveChart]);

    return (
        <div ref={containerRef} className={styles.lineChart} data-qa="line-chart">
            <ChartContext.Provider value={{ chartData, width, height, containerRef, chartDimension }}>
                {customChart ? (
                    children
                ) : (
                    <svg
                        className={styles.lineChartSvg}
                        style={{ overflow: 'visible' }}
                        width={width}
                        height={height}
                        viewBox={`0 0 ${width} ${height}`}
                    >
                        {renderAdditionalContentBefore && renderAdditionalContentBefore(chartData)}
                        {children}
                        {renderAdditionalContentAfter && renderAdditionalContentAfter(chartData)}
                    </svg>
                )}
            </ChartContext.Provider>
        </div>
    );
};

export default Chart;
